import React, { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import { isContentVisible } from "../utils/contentVisibility";
import PortfolioSections from "../components/PortfolioSections";
import { defaultSiteCopy, SiteCopy } from "../utils/siteCopy";

// Set axios base URL
const configuredApiBaseUrl = process.env.REACT_APP_API_BASE_URL;
axios.defaults.baseURL = configuredApiBaseUrl?.startsWith("http://") && window.location.protocol === "https:"
    ? ""
    : configuredApiBaseUrl || (window.location.protocol === "https:" ? "" : "http://localhost:5013");

const HomeContainer = styled.div`
    position: relative;
    width: 100%;
    height: calc(100vh - 140px); /* Subtract header (~80px) and footer (~60px) */
    overflow: hidden;
    background: #000;
    margin: 0;
    padding: 0;

    @media (max-width: 768px) {
        height: calc(100vh - 120px); /* Adjust for mobile header/footer */
    }
`;

const VideoContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    margin: 0;
    padding: 0;
`;

const YouTubeIframe = styled.iframe`
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    min-width: 100%;
    min-height: 100%;
    transform: translate(-50%, -50%);
    border: none;
    pointer-events: none;

    /* Ensure the video covers the entire container regardless of aspect ratio */
    @media (orientation: portrait) {
        width: 177.78vh; /* 16:9 aspect ratio to cover full height */
        height: 100%;
    }

    @media (orientation: landscape) {
        width: 100%;
        height: 56.25vw; /* 16:9 aspect ratio to cover full width */
        min-height: 100%;
    }
`;

const Overlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: white;
    text-align: center;
    padding: 2rem;
`;

const HomeTitle = styled.h1`
    font-size: 4rem;
    font-weight: 700;
    margin-bottom: 1rem;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);

    @media (max-width: 768px) {
        font-size: 2.5rem;
    }

    @media (max-width: 480px) {
        font-size: 2rem;
    }
`;

const HomeSubtitle = styled.p`
    font-size: 1.5rem;
    font-weight: 300;
    max-width: 800px;
    line-height: 1.6;
    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.8);

    @media (max-width: 768px) {
        font-size: 1.2rem;
    }

    @media (max-width: 480px) {
        font-size: 1rem;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background: #000;
    color: white;
    font-size: 1.5rem;
`;

interface Content {
    id: number;
    title: string;
    description: string;
    content_type: "works" | "product" | "pr" | "hero_video";
    category: string;
    youtube_url: string;
    youtube_id: string;
    thumbnail_url: string;
    duration: string;
    published_at: string;
    view_count: number;
    like_count: number;
    custom_thumbnail: string;
    custom_description: string;
    custom_published_at: string;
    is_active: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
}

const Home: React.FC = () => {
    const [heroVideo, setHeroVideo] = useState<Content | null>(null);
    const [siteCopy, setSiteCopy] = useState<SiteCopy>(defaultSiteCopy);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchHeroVideo();
    }, []);

    const fetchHeroVideo = async () => {
        try {
            setLoading(true);
            const [response, copyResponse] = await Promise.all([
                axios.get(`/api/content?type=hero_video`),
                axios.get("/api/site-copy").catch(() => ({ data: { data: defaultSiteCopy } })),
            ]);
            setSiteCopy({ ...defaultSiteCopy, ...(copyResponse.data.data || {}) });
            if (response.data.success && response.data.data.length > 0) {
                const visibleVideos = (response.data.data || []).filter(isContentVisible);
                setHeroVideo(visibleVideos[0] || null);
            }
        } catch (error) {
            console.error("Error fetching hero video:", error);
        } finally {
            setLoading(false);
        }
    };

    const getYouTubeEmbedUrl = (youtubeId: string) => {
        return `https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&disablekb=1`;
    };

    if (loading) {
        return (
            <LoadingContainer>
                Loading...
            </LoadingContainer>
        );
    }

    if (!heroVideo) {
        return (
            <>
                <HomeContainer>
                    <Overlay>
                        <HomeTitle>{siteCopy.home_fallback_title}</HomeTitle>
                        <HomeSubtitle>{siteCopy.home_fallback_subtitle}</HomeSubtitle>
                    </Overlay>
                </HomeContainer>
                <PortfolioSections />
            </>
        );
    }

    return (
        <>
            <HomeContainer>
                <VideoContainer>
                    <YouTubeIframe
                        src={getYouTubeEmbedUrl(heroVideo.youtube_id)}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </VideoContainer>
                <Overlay>
                    <HomeTitle>{heroVideo.title}</HomeTitle>
                    <HomeSubtitle>{heroVideo.custom_description || heroVideo.description}</HomeSubtitle>
                </Overlay>
            </HomeContainer>
            <PortfolioSections />
        </>
    );
};

export default Home;
